import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { T, useT } from "../i18n";
import { PfeilLinks } from "../schale/Kopf";
import { useTelefon } from "../schale/geraet";
import { ATTRAPPE } from "../daten/klient";
import { Fassung } from "../bausteine/Fassung";
import { Faktorbild } from "../bausteine/Faktorbild";
import { Zeichen } from "../bausteine/Zeichen";
import type { Anmeldung as Angemeldet } from "../daten/typen";
import { Anmeldesperre, MINDESTLAENGE, useAnmeldelauf, usePasswortwechsel } from "./anmeldelauf";
import { AnmeldungTelefon } from "./AnmeldungTelefon";
import { LogoBild } from "../schale/LogoBild";

/* ══════════════════════════════════════════════════════════════════
   Anmeldung — zwei Stufen.

   Links die Markenfläche (seit Signal, 07.10.2026: Hüllenfarbe, volles
   Logo, kein Trecker und kein Werbesatz mehr), rechts das Formular. Die
   Fassungsnummer steht unten in der Marke: Wer eine Fassung nennen kann,
   kann einen Fehler melden, den jemand wiederfindet.

   Selbstregistrierung ist abgeschaltet (25-anmeldung-und-rechte.md) —
   wer ein Konto braucht, bekommt es von der Verwaltung. Das steht auch
   da, damit niemand danach sucht.

   **Am Telefon ist es eine andere Maske** (19.09.2026,
   `AnmeldungTelefon.tsx`); der Ablauf dahinter ist für beide derselbe
   (`anmeldelauf.tsx`). Hier steht nur noch die Fassung des Schreibtischs.
   ══════════════════════════════════════════════════════════════════ */

function MarkeSeite() {
  const t = useT();
  return (
    /* **Signal (07.10.2026, docs/64, Paket 2):** die Markenfläche in der Farbe
       der Hülle, das volle Logo mit Leistungszeile, unten die Fassung. Kein
       Werbesatz, kein Trecker — Luca: *„dieser Satz kann auf dem Login-Screen
       komplett weg, weil er da nichts zu suchen hat."* Die drei Säulen standen
       als Versalien darunter und sagten dasselbe wie die Leistungszeile im
       Logo. Die Fassung bleibt: Wer einen Fehler meldet, nennt sie. */
    <aside className="anmeldung-marke">
      <LogoBild art="dunkel" alt={t("app.logoAlt")} />
      <div className="marke-fassung">{t("app.name")} <Fassung /></div>
    </aside>
  );
}

function Seite({ children }: { children: ReactNode }) {
  return (
    <div className="anmeldung">
      <MarkeSeite />
      <div className="anmeldung-feld">
        <div className="anmeldung-innen">{children}</div>
      </div>
    </div>
  );
}

/**
 * **Das Wechselfenster** (16.09.2026, Migration 0101).
 *
 * Luca: *„Dann muss auch ein Pop-up kommen, sodass man sich nur wirklich zum
 * Ende anmelden kann. Im Log-in-Bereich schon, dass man das Passwort dort
 * schon ändern muss."* Bis dahin ließ die Anmeldung jemanden mit fälligem
 * Wechsel hinein, und drinnen hing jede Maske an einer 403 — eine Maske zum
 * Wechseln gab es nicht.
 *
 * Jetzt antwortet die Anmeldung mit `403 anmeldung.passwortwechsel_faellig`,
 * ohne eine Sitzung auszustellen, und dieses Fenster legt sich über die
 * Anmeldung. Hinein geht es erst mit dem neuen Passwort. „Abbrechen" führt
 * zurück zum Formular, nicht in die Anwendung.
 *
 * Kein `Deckblatt`: Das hängt am Arbeitsbereich der Schale, und die gibt es
 * vor der Anmeldung noch nicht. Also ein eigenes Fenster, per Portal an
 * `body` — im Formular hinge es sonst im Schnitt von `.anmeldung-feld`.
 */
function PasswortWechsel({ mail, altes, schliessen, drin }: {
  mail: string;
  altes: string;
  schliessen: () => void;
  drin: (antwort: Angemeldet) => void;
}) {
  const t = useT();
  const id = useId();
  const { neu, setzeNeu, nochmal, setzeNochmal, fehler, laeuft, speichern } =
    usePasswortwechsel({ mail, altes, drin });
  const erstes = useRef<HTMLInputElement>(null);

  useEffect(() => { erstes.current?.focus(); }, []);

  // `Esc` schließt wie überall — solange nichts unterwegs ist.
  useEffect(() => {
    const taste = (e: KeyboardEvent) => { if (e.key === "Escape" && !laeuft) schliessen(); };
    document.addEventListener("keydown", taste);
    return () => document.removeEventListener("keydown", taste);
  }, [laeuft, schliessen]);

  return createPortal(
    <div className="wechsel-schirm">
      <div className="wechsel-blatt" role="dialog" aria-modal="true" aria-labelledby={`${id}-titel`}>
        <h3 id={`${id}-titel`}>{t("anmeldung.wechselTitel")}</h3>
        <p className="anmeldung-unter">{t("anmeldung.wechselUnter")}</p>
        <form
          className="formular"
          onSubmit={(e) => { e.preventDefault(); void speichern(); }}
        >
          {/* Für Passwortverwalter: Zu welchem Konto das neue gehört. */}
          <input type="email" autoComplete="username" value={mail} readOnly hidden />
          <div>
            <label className="feld-schild" htmlFor={`${id}-neu`}>{t("anmeldung.wechselNeu")}</label>
            <input
              ref={erstes}
              className="feld" id={`${id}-neu`} type="password" autoComplete="new-password"
              autoCapitalize="off" autoCorrect="off" spellCheck={false}
              value={neu} onChange={(e) => setzeNeu(e.target.value)}
            />
            <p className="wechsel-hinweis">{t("anmeldung.wechselHinweis", { n: MINDESTLAENGE })}</p>
          </div>
          <div>
            <label className="feld-schild" htmlFor={`${id}-nochmal`}>{t("anmeldung.wechselNochmal")}</label>
            <input
              className="feld" id={`${id}-nochmal`} type="password" autoComplete="new-password"
              autoCapitalize="off" autoCorrect="off" spellCheck={false}
              value={nochmal} onChange={(e) => setzeNochmal(e.target.value)}
            />
          </div>
          {fehler ? <p className="code-fehler" role="alert">{fehler}</p> : null}
          <button className="knopf haupt knopf-breit" type="submit" disabled={laeuft}>
            {t("anmeldung.wechselKnopf")}
          </button>
          <button className="knopf stumm knopf-breit" type="button" disabled={laeuft} onClick={schliessen}>
            {t("anmeldung.wechselAbbrechen")}
          </button>
        </form>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Die Anmeldung — am Telefon die eigene Maske, sonst die des Schreibtischs.
 * Welche es ist, sagt das Gerät (`schale/geraet.ts`), nicht die Adresse.
 */
export function Anmeldung() {
  return useTelefon() ? <AnmeldungTelefon /> : <AnmeldungSchirm />;
}

function AnmeldungSchirm() {
  const t = useT();
  const {
    stufe, zurueckZurAnmeldung, mail, setzeMail, wort, setzeWort, merken, setzeMerken, leer, hinweis,
    fehler, laeuft, wechsel, setzeWechsel, ziffern, falsch, felder, einrichtung,
    anmeldenVersuchen, gewechselt, codePruefen, zifferSetzen, taste, einfuegen,
  } = useAnmeldelauf();
  const [zeigen, setzeZeigen] = useState(false);
  const [vergessen, setzeVergessen] = useState(false);

  if (stufe === "code") {
    return (
      <Seite>
        {/* Steht schon links über dem Titel, wie jeder Rückweg. Ein Knopf,
            kein Verweis: Die Stufe wechselt, nicht die Adresse. Der Pfeil
            ist derselbe wie im Kopf der Masken. */}
        <button className="zurueck-link" type="button" onClick={zurueckZurAnmeldung}>
          <PfeilLinks />
          {t("allgemein.zurueck")}
        </button>
        <h3>{t(einrichtung ? "faktor.einrichtenTitel" : "anmeldung.codeTitel")}</h3>
        <p className="anmeldung-unter">{t(einrichtung ? "faktor.einrichtenUnter" : "anmeldung.codeUnter")}</p>
        {/* Noch kein Faktor, aber verlangt (E4): erst das Bild, dann der Code. */}
        {einrichtung ? <Faktorbild e={einrichtung} /> : null}
        <div className="formular">
          <div>
            <span className="feld-schild">{t("anmeldung.codeSchild")}</span>
            <div className="ziffern">
              {ziffern.map((z, i) => (
                <input
                  key={i}
                  ref={(el) => { felder.current[i] = el; }}
                  className={`ziffer${z ? " voll" : ""}${falsch ? " falsch" : ""}`}
                  inputMode="numeric"
                  /* Am ersten Feld bietet das Telefon den Code aus der SMS
                     selbst an; an den übrigen wäre dasselbe Angebot sechsmal
                     dieselbe Frage. `maxLength` steht dem nicht im Weg — es
                     begrenzt das Tippen, nicht das Einsetzen. */
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  value={z}
                  aria-label={t("anmeldung.codeZiffer", { n: i + 1 })}
                  onChange={(e) => zifferSetzen(i, e.target.value)}
                  onKeyDown={(e) => taste(i, e)}
                  onPaste={einfuegen}
                />
              ))}
            </div>
            <p className="code-fehler" role="alert">{fehler ?? ""}</p>
          </div>
          <button
            className="knopf haupt knopf-breit"
            type="button"
            disabled={laeuft}
            onClick={() => codePruefen(ziffern.join(""))}
          >
            {t("anmeldung.knopf")}
          </button>
          <button className="knopf stumm knopf-breit" type="button">
            {t("anmeldung.codeAnderes")}
          </button>
        </div>
        {ATTRAPPE ? <div className="mfa-tipp"><T k="anmeldung.codeTipp" /></div> : null}
      </Seite>
    );
  }

  return (
    <Seite>
      <h3>{t("anmeldung.titel")}</h3>
      <p className="anmeldung-unter">{t("anmeldung.unter")}</p>
      {/* **Ohne Netz kommt hier niemand hinein** — und das ist keine Lücke,
          sondern die Folge davon, dass das Kontext-Token im Speicher lebt und
          die Sitzung serverseitig erneuert wird (`POST /api/kontext`). Wer im
          Funkloch die App neu startet, landet genau hier. Dann soll dastehen,
          was los ist und dass nichts verloren ist (0113). */}
      <Anmeldesperre />
      {hinweis ? <p className="netzband" role="status"><span>{hinweis}</span></p> : null}
      <form
        className="formular"
        onSubmit={(e) => { e.preventDefault(); void anmeldenVersuchen(); }}
      >
        <div>
          <label className="feld-schild" htmlFor="an-mail">{t("anmeldung.mail")}</label>
          {/* **Die Tastatur des Telefons entscheidet mit, wer hineinkommt**
              (17.09.2026). `inputMode` holt das @ auf die erste Ebene,
              `autoCapitalize`/`autoCorrect` halten das Telefon davon ab, aus
              „luca" ein „Luca" und aus einer Hausdomäne ein Wörterbuchwort zu
              machen. Bei `type="email"` schalten beide großen Systeme das
              zwar meist von selbst ab — „meist" ist bei einer Anmeldung zu
              wenig, und geschrieben kostet es nichts. */}
          <input
            className="feld" id="an-mail" type="email" autoComplete="username"
            inputMode="email" autoCapitalize="off" autoCorrect="off" spellCheck={false}
            value={mail} onChange={(e) => setzeMail(e.target.value)}
            aria-invalid={leer === "mail" || undefined}
          />
        </div>
        <div>
          <div className="feld-kopf">
            <label className="feld-schild" htmlFor="an-wort" style={{ margin: 0 }}>
              {t("anmeldung.passwort")}
            </label>
            {/* **Ein Verweis, der etwas sagt** (07.10.2026). Bis hierher stand
                „Passwort vergessen — Link per Mail" da und führte nirgendwohin
                (docs/47, „Drei Dinge auf diesem Schirm tun heute noch nichts").
                Einen Mailweg gibt es noch nicht; der Verweis sagt jetzt, wer
                weiterhilft, statt nichts zu tun. */}
            <button
              type="button"
              className="anm-verweis"
              aria-expanded={vergessen}
              aria-controls="an-vergessen"
              onClick={() => setzeVergessen((x) => !x)}
            >
              {t("anmeldung.passwortVergessen")}
            </button>
          </div>
          {/* Ein Passwortfeld verrät dem Telefon nicht, was darin steht — und
              manche Tastaturen schreiben darum trotzdem groß an. Wer sein
              Passwort mit großem Anfangsbuchstaben eintippt, weil das Gerät
              es so wollte, kommt nicht hinein und sucht den Fehler bei sich. */}
          <div className="eingabe-mit-knopf">
            <input
              className="feld" id="an-wort" type={zeigen ? "text" : "password"} autoComplete="current-password"
              autoCapitalize="off" autoCorrect="off" spellCheck={false}
              value={wort} onChange={(e) => setzeWort(e.target.value)}
              aria-invalid={leer === "wort" || undefined}
            />
            <button
              type="button"
              className="anm-zeigen"
              aria-pressed={zeigen}
              aria-label={t(zeigen ? "anmeldung.passwortVerbergenSchild" : "anmeldung.passwortZeigenSchild")}
              onClick={() => setzeZeigen((z) => !z)}
            >
              {t(zeigen ? "anmeldung.passwortVerbergen" : "anmeldung.passwortZeigen")}
            </button>
          </div>
          {vergessen ? <p className="anm-hinweis" id="an-vergessen" role="status">{t("anmeldung.passwortVergessenSatz")}</p> : null}
        </div>
        <div className="merken">
          <input
            type="checkbox" id="an-merken"
            checked={merken} onChange={(e) => setzeMerken(e.target.checked)}
          />
          <label htmlFor="an-merken">{t("anmeldung.merken")}</label>
        </div>
        {fehler ? (
          <div className="anm-meldung" role="alert">
            <Zeichen name="warnung" />
            <span>{fehler}</span>
          </div>
        ) : null}
        <button className="knopf haupt knopf-breit" type="submit" disabled={laeuft}>
          {t("anmeldung.knopf")}
        </button>
      </form>
      <p className="anmeldung-fuss">{t("anmeldung.keinKonto")}</p>
      {wechsel ? (
        <PasswortWechsel
          mail={mail}
          altes={wort}
          schliessen={() => setzeWechsel(false)}
          drin={gewechselt}
        />
      ) : null}
    </Seite>
  );
}
